import { apiRequest } from "@common/api";
import type { FetchOptions } from "@common/fetch-logger";

// A save state is a few hundred KB to a few MB through the API, so its
// bytes get longer than a JSON call's 8 s.
const SAVE_BYTES: FetchOptions = { timeoutMs: 60_000 };

// play-api's save routes:
//   GET    /saves                 -> CloudSaveMeta[]
//   GET    /saves/{name}          -> application/octet-stream
//   PUT    /saves/{name}          <- application/octet-stream
//   DELETE /saves/{name}
// Names are mGBA's own (<base>.sav, <base>.ss1..3); saves are scoped to the
// caller's sub server-side.

export interface CloudSaveMeta {
  name: string;
  size: number;
  updatedAt: string;
}

export const cloudSaves = {
  // `signal` cancels the request: leaving the player gives up on a slow sync.
  async list(signal?: AbortSignal): Promise<CloudSaveMeta[]> {
    return (await apiRequest("/saves", { signal })).json();
  },

  async upload(name: string, bytes: Uint8Array, signal?: AbortSignal): Promise<void> {
    await apiRequest(`/saves/${encodeURIComponent(name)}`, {
      method: "PUT",
      signal,
      headers: { "Content-Type": "application/octet-stream" },
      body: bytes as BodyInit,
    }, SAVE_BYTES);
  },

  async download(name: string, signal?: AbortSignal): Promise<Uint8Array> {
    const response = await apiRequest(`/saves/${encodeURIComponent(name)}`, { signal }, SAVE_BYTES);
    return new Uint8Array(await response.arrayBuffer());
  },

  async remove(name: string, signal?: AbortSignal): Promise<void> {
    await apiRequest(`/saves/${encodeURIComponent(name)}`, { method: "DELETE", signal });
  },
};
